import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { FiPlus, FiEdit2, FiTrash2, FiX, FiSave } from "react-icons/fi";
import { useApp } from "../../context/AppContext";
import inventoryService from "../../services/inventoryService";
import ConfirmModal from "../../components/common/ConfirmModal";

const PRESET_COLORS = [
    "#003b67",
    "#059669",
    "#7c3aed",
    "#dc2626",
    "#d97706",
    "#0891b2",
    "#6b7280",
    "#db2777",
];

const EMPTY_FORM = { name: "", description: "", color: "#003b67" };

function CategoryModal({ initial, onSave, onClose, saving }) {
    const [form, setForm] = useState({ ...EMPTY_FORM, ...initial });
    return (
        <div className="modal-overlay" onClick={onClose}>
            <div
                className="modal"
                style={{ width: 420 }}
                onClick={(e) => e.stopPropagation()}
            >
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "1rem",
                    }}
                >
                    <h3 style={{ margin: 0 }}>
                        {initial?._id ? "Edit Category" : "Add Category"}
                    </h3>
                    <button
                        onClick={onClose}
                        style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            color: "var(--text-muted)",
                        }}
                    >
                        <FiX size={18} />
                    </button>
                </div>
                <div style={{ display: "grid", gap: "0.75rem" }}>
                    <div className="form-group">
                        <label className="form-label">
                            Name <span style={{ color: "var(--danger)" }}>*</span>
                        </label>
                        <input
                            className="form-control"
                            value={form.name}
                            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                            placeholder="e.g. Lab Equipment"
                            autoFocus
                        />
                    </div>
                    <div className="form-group">
                        <label className="form-label">Description</label>
                        <textarea
                            className="form-control"
                            rows={2}
                            style={{ resize: "vertical" }}
                            value={form.description}
                            onChange={(e) =>
                                setForm((p) => ({ ...p, description: e.target.value }))
                            }
                            placeholder="Optional description…"
                        />
                    </div>
                    <div className="form-group">
                        <label className="form-label">Color</label>
                        <div
                            style={{
                                display: "flex",
                                gap: "0.4rem",
                                flexWrap: "wrap",
                                alignItems: "center",
                            }}
                        >
                            {PRESET_COLORS.map((c) => (
                                <div
                                    key={c}
                                    onClick={() => setForm((p) => ({ ...p, color: c }))}
                                    style={{
                                        width: 24,
                                        height: 24,
                                        borderRadius: "50%",
                                        background: c,
                                        cursor: "pointer",
                                        outline: form.color === c ? `3px solid ${c}` : "none",
                                        outlineOffset: 2,
                                    }}
                                />
                            ))}
                            <input
                                type="color"
                                value={form.color}
                                onChange={(e) =>
                                    setForm((p) => ({ ...p, color: e.target.value }))
                                }
                                style={{
                                    width: 32,
                                    height: 24,
                                    border: "1px solid var(--border)",
                                    borderRadius: 4,
                                    cursor: "pointer",
                                    padding: 0,
                                }}
                            />
                        </div>
                    </div>
                </div>
                <div className="modal-actions" style={{ marginTop: "1.25rem" }}>
                    <button className="btn btn-outline" onClick={onClose}>
                        Cancel
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={() => onSave(form)}
                        disabled={saving || !form.name}
                    >
                        <FiSave size={13} /> {saving ? "Saving…" : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function CategoryList() {
    const { academicYear } = useApp();
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(null);
    const [saving, setSaving] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const fetchCategories = async () => {
        setLoading(true);
        try {
            const res = await inventoryService.getCategories(
                academicYear?._id ? { academicYear: academicYear._id } : {}
            );
            setCategories(res.data);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCategories();
    }, [academicYear?._id]);

    const handleSave = async (form) => {
        setSaving(true);
        try {
            if (form._id) {
                const res = await inventoryService.updateCategory(form._id, {
                    ...form,
                    academicYear: academicYear?._id,
                });
                toast.success(res.message);
            } else {
                const res = await inventoryService.createCategory({
                    ...form,
                    academicYear: academicYear?._id,
                });
                toast.success(res.message);
            }
            setModal(null);
            fetchCategories();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            const res = await inventoryService.deleteCategory(deleteTarget._id);
            toast.success(res.message);
            setDeleteTarget(null);
            fetchCategories();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div>
            <div className="page-header">
                <div>
                    <h1 className="page-title">Inventory Categories</h1>
                    <p className="page-subtitle">{categories.length} categories</p>
                </div>
                <button
                    className="btn btn-primary"
                    onClick={() => setModal({ initial: EMPTY_FORM })}
                >
                    <FiPlus size={14} /> Add Category
                </button>
            </div>

            <div className="card">
                {loading ? (
                    <div className="loading-center">
                        <div className="spinner" />
                        Loading…
                    </div>
                ) : categories.length === 0 ? (
                    <div className="empty-state">
                        <p>No categories yet. Add one to get started.</p>
                    </div>
                ) : (
                    <div className="table-wrapper">
                        <table>
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Category</th>
                                    <th>Description</th>
                                    <th style={{ textAlign: "center" }}>Items</th>
                                    <th style={{ textAlign: "right" }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {categories.map((cat, i) => (
                                    <tr key={cat._id}>
                                        <td style={{ fontSize: 12, color: "var(--text-muted)" }}>
                                            {i + 1}
                                        </td>
                                        <td>
                                            <div
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "0.5rem",
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        width: 12,
                                                        height: 12,
                                                        borderRadius: "50%",
                                                        background: cat.color,
                                                        flexShrink: 0,
                                                    }}
                                                />
                                                <span style={{ fontWeight: 600 }}>{cat.name}</span>
                                            </div>
                                        </td>
                                        <td style={{ fontSize: 13, color: "var(--text-muted)" }}>
                                            {cat.description || "—"}
                                        </td>
                                        <td style={{ textAlign: "center", fontSize: 13 }}>
                                            {cat.itemCount}
                                        </td>
                                        <td>
                                            <div
                                                style={{
                                                    display: "flex",
                                                    gap: "0.35rem",
                                                    justifyContent: "flex-end",
                                                }}
                                            >
                                                <button
                                                    className="btn btn-outline btn-sm"
                                                    onClick={() => setModal({ initial: cat })}
                                                >
                                                    <FiEdit2 size={12} />
                                                </button>
                                                <button
                                                    className="btn btn-danger btn-sm"
                                                    onClick={() => setDeleteTarget(cat)}
                                                >
                                                    <FiTrash2 size={12} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {modal && (
                <CategoryModal
                    initial={modal.initial}
                    onSave={handleSave}
                    onClose={() => setModal(null)}
                    saving={saving}
                />
            )}
            {deleteTarget && (
                <ConfirmModal
                    title="Delete Category"
                    message={`Delete "${deleteTarget.name}"? ${deleteTarget.itemCount > 0 ? `${deleteTarget.itemCount} items use this category — delete or reassign them first.` : ""}`}
                    onConfirm={handleDelete}
                    onCancel={() => setDeleteTarget(null)}
                    loading={deleting}
                />
            )}
        </div>
    );
}
